import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled, { withTheme } from "styled-components";
import { connect } from "react-redux";
import DailyTargets from "../../library/Goals/DailyTargets";
import GoalsSectionTitle from "../../ui-library/HeaderTitles/GoalsSectionTitle";
import HorizontalProgressBarChart from "../../ui-library/HorizontalProgressBarChart/HorizontalProgressBarChart";
import EntriesCard from "../../library/EntriesCard/EntriesCard";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import DietDiary from "../DietDiary/DietDiary";
import CustomModal, {
  ModalSize,
} from "../../ui-library/CustomModal/CustomModal";
import DaySummary from "../../library/DaySummary/DaySummary";
import {
  dailyTargetIds,
  DateFormat,
  getMacronutrientColor,
  kcalMultiplier,
} from "../../../modules/constants";
import LoadingSpinner from "../../ui-library/LoadingSpinner/LoadingSpinner";
import { GoalLevels } from "../../library/Goals/Constants";
import moment from "moment";
import {
  dailyEntries,
  mapDispatchToProps,
  mapStateToProps,
} from "./DailyEntries.store";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../DietDiary/DietDiary.constants";
import { useTranslation } from "react-i18next";
import { decimalPlaces, roundMealValue } from "../../../helpers/mealsHelper";

const EmptyDataBoard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelsWrapper = styled.div`
  margin-left: 0px;
  margin-right: 0px;
  width: 100%;

  & > div {
    margin-right: 15px;
    margin-bottom: 15px;
    flex: 0.45;
  }
  & > #entries-list {
    min-width: 50%;
    flex: 1;
    margin-left: 20px;
    > div {
      box-shadow: none;
      height: 100%;
    }
  }

  @media (max-width: 1279px) {
    flex-direction: column;
    & > #entries-list {
      min-width: unset;
      width: inherit;
      margin-left: 0;
    }
    & > div {
      margin-right: 0;
    }
  }
`;

const HorizontalProgressBarChartWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const DailyTargetsWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const dataStatsArrEmpty = [
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.CALORIES_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.CARBS_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.SUGAR_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.FATS_ATTR_ID,
  },
  {
    frequency: 0,
    count: 0,
    id: dailyTargetIds.PROTEIN_ATTR_ID,
  },
];

export const DailyEntries = (props) => {
  const {
    date,
    goalsChartTitle,
    listTitle,
    listActionTitle,
    closeDietDiaryAction,
    fetchEntries,
    completeDay,
    loadingEntries,
    loadingEntriesError,
    entries,
    studyId,
    canCompleteDay,
    canViewMacronutrients,
    canEditMeal,
    refetchData,
    dailyGoals,
    theme,
  } = props;

  const { t } = useTranslation();

  const dailyTargetsArr = useMemo(() => {
    return {
      goalTitle: t("appText.dietDiary.targets.target"),
      calories: {
        calories: `${roundMealValue(dailyGoals?.calories ?? 0)}`,
        title: t("appText.dietDiary.calories"),
      },
      goalsList: [
        {
          goalLabel: t("appText.dietDiary.protein"),
          goalLevel: GoalLevels.greaterOrEqual,
          goalColor: getMacronutrientColor(
            dailyTargetIds.PROTEIN_ATTR_ID,
            theme,
            studyId
          ),
          goalValue: `${roundMealValue(dailyGoals?.proteinPercentage ?? 0)}%`,
        },
        {
          goalLabel: t("appText.dietDiary.carbohydrates"),
          goalLevel: GoalLevels.less,
          goalColor: getMacronutrientColor(
            dailyTargetIds.CARBS_ATTR_ID,
            theme,
            studyId
          ),
          goalValue: `${roundMealValue(
            dailyGoals?.carbohydratesPercentage ?? 0
          )}%`,
        },
      ],
      goalMessage: t("contentText.dietDiary.targets.sugars"),
    };
  }, [dailyGoals, studyId, t, theme]);

  const isDayCompleted = useMemo(
    () => entries?.meals[0]?.completed || entries?.meals?.length === 0,
    [entries]
  );

  const [completeDayData, setCompleteDayData] = useState(null);

  const [selectedDietDiaryOption, setSelectedDietDiaryOption] = useState(null);
  const [dietDiaryModalTitle, setDietDiaryModalTitle] = useState("");

  const [dataStats, setDataStats] = useState(dataStatsArrEmpty);
  const [goalsChartsData, setGoalChartsData] = useState({
    title: goalsChartTitle,
    headerData: [
      {
        label: t("appText.dietDiary.calories"),
        value: `0 kcal`,
        color: props.theme?.magentaShade,
      },
    ],
    data: [
      {
        label: t("appText.dietDiary.protein"),
        value: `0`,
        color: getMacronutrientColor(
          dailyTargetIds.PROTEIN_ATTR_ID,
          theme,
          studyId
        ),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.carbohydrates"),
        value: `0`,
        color: getMacronutrientColor(
          dailyTargetIds.CARBS_ATTR_ID,
          theme,
          studyId
        ),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.fats"),
        value: `0`,
        color: getMacronutrientColor(
          dailyTargetIds.FATS_ATTR_ID,
          theme,
          studyId
        ),
        unit: "%",
      },
      {
        label: t("appText.dietDiary.sugar"),
        value: `0`,
        hideBar: true,
        unit: "g",
      },
    ],
  });
  const [formatedEntries, setFormatedEntries] = useState(null);

  const formatEntries = useCallback(
    (entries) => {
      const finalArray = [];
      if (entries) {
        entries.meals.forEach((elem) => {
          const mealsItems = [];
          let jsonData = elem?.mealJson;
          jsonData =
            typeof jsonData === "string" ? JSON.parse(elem.mealJson) : jsonData;

          let rowTitle = "";
          let rowDate = null;
          if (jsonData) {
            jsonData.items.forEach((jsonElem) => {
              rowTitle += `${jsonElem.title}, `;
              rowDate = elem.mealDate;
              mealsItems.push({
                title: jsonElem.title,
                date: elem.mealDate,
                dayDate: date,
                elem: elem,
              });
            });
          }

          // removing last comma
          rowTitle = rowTitle.slice(0, -2);

          if (mealsItems.length > 0) {
            finalArray.push({
              title: rowTitle,
              key: elem.key,
              completed: elem.completed,
              json: elem.mealJson,
              date: rowDate,
              reason: elem.reason,
              items: mealsItems,
            });
          }
        });
      }

      finalArray.sort((elemPrev, elemNext) =>
        elemNext.date.localeCompare(elemPrev.date)
      );
      return finalArray;
    },
    [date]
  );

  useEffect(() => {
    if (entries && entries.meals && entries.meals.length) {
      const formatedEntriesData = formatEntries(entries);
      setFormatedEntries(formatedEntriesData);
    } else {
      setFormatedEntries([]);
    }
  }, [formatEntries, entries]);

  useEffect(() => {
    if (formatedEntries) {
      const totalCaloriesCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return total + jsonData?.aboutMeal?.Calories?.value;
      }, 0);

      const totalCarbsCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return total + jsonData?.aboutMeal?.Carbs?.value;
      }, 0);

      const totalProteinCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return total + jsonData?.aboutMeal?.Protein?.value;
      }, 0);

      const totalFatCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return total + jsonData?.aboutMeal?.Fats?.value;
      }, 0);

      const totalSugarCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return total + jsonData?.aboutMeal?.Sugars?.value;
      }, 0);

      let carbsFreq = 0;
      let protFreq = 0;
      let fatFreq = 0;

      // to avoid division by 0
      if (totalCaloriesCount > 0) {
        carbsFreq =
          ((totalCarbsCount * kcalMultiplier.carbs) / totalCaloriesCount) * 100;
        protFreq =
          ((totalProteinCount * kcalMultiplier.protein) / totalCaloriesCount) *
          100;
        fatFreq =
          ((totalFatCount * kcalMultiplier.fats) / totalCaloriesCount) * 100;
      }

      const dataStatsArr = [
        {
          frequency: 0,
          count: totalCaloriesCount,
          id: dailyTargetIds.CALORIES_ATTR_ID,
        },
        {
          frequency: carbsFreq,
          count: totalCarbsCount,
          id: dailyTargetIds.CARBS_ATTR_ID,
        },
        {
          frequency: 0,
          count: totalSugarCount,
          id: dailyTargetIds.SUGAR_ATTR_ID,
        },
        {
          frequency: fatFreq,
          count: totalFatCount,
          id: dailyTargetIds.FATS_ATTR_ID,
        },
        {
          frequency: protFreq,
          count: totalProteinCount,
          id: dailyTargetIds.PROTEIN_ATTR_ID,
        },
      ];

      const goalsChartArr = {
        title: goalsChartTitle,
        headerData: [
          {
            label: t("appText.dietDiary.calories"),
            value: `${roundMealValue(totalCaloriesCount).toFixed(
              decimalPlaces
            )} kcal`,
            color: props.theme?.magentaShade,
          },
        ],
        data: [
          {
            label: t("appText.dietDiary.protein"),
            value: `${roundMealValue(protFreq)}`,
            color: getMacronutrientColor(
              dailyTargetIds.PROTEIN_ATTR_ID,
              theme,
              studyId
            ),
            unit: "%",
          },
          {
            label: t("appText.dietDiary.carbohydrates"),
            value: `${roundMealValue(carbsFreq)}`,
            color: getMacronutrientColor(
              dailyTargetIds.CARBS_ATTR_ID,
              theme,
              studyId
            ),
            unit: "%",
          },
          {
            label: t("appText.dietDiary.fats"),
            value: `${roundMealValue(fatFreq)}`,
            color: getMacronutrientColor(
              dailyTargetIds.FATS_ATTR_ID,
              theme,
              studyId
            ),
            unit: "%",
          },
          {
            label: t("appText.dietDiary.sugar"),
            value: `${roundMealValue(totalSugarCount)}`,
            hideBar: true,
            unit: "g",
          },
        ],
      };

      setDataStats(dataStatsArr);
      setGoalChartsData(goalsChartArr);
    }
  }, [formatedEntries, date, goalsChartTitle, props.theme, theme, studyId, t]);

  const finishDayConfirm = () => {
    setCompleteDayData({
      day: date.date,
      dayElements: dataStats,
    });
  };

  const finishDay = () => {
    setCompleteDayData(null);
    completeDay({ day: moment(date.date).format(DateFormat.INVERTED_DATE) })
      .then((message) => {
        refetchData(date);
        sendNotification(message, ToastType.SUCCESS);
      })
      .catch((e) => sendNotification(e, ToastType.ERROR));
  };

  const entryEdit = (element) => {
    const type = JSON.parse(element.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.edit")} ${dietDairyTypeToString(type)}`
    );

    setSelectedDietDiaryOption({
      mealId: element.key,
      jsonData: element.json,
      date: element.date,
      type: type,
      readOnly: element.readOnly,
      reason: element.reason,
      reasonForChange: element.reasonForChange,
    });
  };

  const onCloseDietDiaryModal = () => {
    setSelectedDietDiaryOption(null);
    if (closeDietDiaryAction) {
      closeDietDiaryAction();
    } else {
      fetchEntries(date);
    }
  };

  const onCloseCompleteDayModal = () => {
    setCompleteDayData(null);
  };

  return (
    <div data-testid="daily-entries">
      <GoalsSectionTitle label={date.label} date={`${date.formatedDate}`} />
      <PanelsWrapper className="row">
        <EntriesCard
          id="entries-list"
          title={
            listTitle || t("contentText.dietDiary.entriesPanel.today.title")
          }
          elements={formatedEntries}
          showActionButton={canCompleteDay}
          actionButtonText={
            listActionTitle || t("appText.dietDiary.completeDay")
          }
          actionButton={finishDayConfirm}
          actionButtonInactive={isDayCompleted}
          loadingEntries={loadingEntries}
          loadingEntriesError={loadingEntriesError}
          onEdit={(elementEdit) => entryEdit(elementEdit)}
          canEdit={canEditMeal && !isDayCompleted}
          key={date.key}
        />

        {canViewMacronutrients && (
          <HorizontalProgressBarChartWrapper>
            {goalsChartsData ? (
              <HorizontalProgressBarChart {...goalsChartsData} />
            ) : (
              <EmptyDataBoard>
                <p>{t("appText.noData")}</p>
              </EmptyDataBoard>
            )}
          </HorizontalProgressBarChartWrapper>
        )}

        {dailyGoals && (
          <DailyTargetsWrapper>
            {dailyTargetsArr ? (
              <DailyTargets {...dailyTargetsArr} />
            ) : (
              <LoadingSpinner />
            )}
          </DailyTargetsWrapper>
        )}
      </PanelsWrapper>

      <CustomModal
        show={selectedDietDiaryOption !== null}
        title={dietDiaryModalTitle ?? ""}
        size={ModalSize.large}
        onClose={() => onCloseDietDiaryModal()}
      >
        <DietDiary
          mealJsonData={selectedDietDiaryOption?.jsonData}
          mealId={selectedDietDiaryOption?.mealId}
          onSubmit={() => onCloseDietDiaryModal()}
          maxDate={moment().toDate()}
          minDate={moment().subtract(1, "days").toDate()}
          mealDate={selectedDietDiaryOption?.date}
          readOnly={selectedDietDiaryOption?.readOnly}
          dietDiaryOption={selectedDietDiaryOption}
          setModalTitle={setDietDiaryModalTitle}
          existingReason={selectedDietDiaryOption?.reason}
          existingReasonForChange={selectedDietDiaryOption?.reasonForChange}
          type={selectedDietDiaryOption?.type}
          studyId={studyId}
        />
      </CustomModal>

      {completeDayData?.dayElements[0] && dataStats !== null && (
        <CustomModal
          show={completeDayData?.dayElements[0] && dataStats !== null}
          title={t("contentText.dietDiary.completeDay.title")}
          size={ModalSize.small}
          onClose={() => onCloseCompleteDayModal()}
        >
          <DaySummary
            completeDayData={completeDayData}
            dataStats={dataStats}
            finishDay={() => finishDay()}
            closeModal={() => onCloseCompleteDayModal()}
            studyId={studyId}
          />
        </CustomModal>
      )}
    </div>
  );
};

export default withTheme(
  dynamic(
    "dailyEntries",
    subspaced({
      // We map extra state because we might need to acess other stores for data, subspace is self contained.
      mapExtraState: (state, rootState) => ({
        loginReducer: { ...state.loginReducer },
      }),
    }),
    attachReducer(dailyEntries)
  )(connect(mapStateToProps, mapDispatchToProps)(DailyEntries))
);
